import { LoadingButton } from "@mui/lab";
import { Box, Grid2, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";

import { useGetDevicesListQuery } from "../../redux/reducers/settings.reducer";
import { EVENT_PRODUCT_TYPE_E } from "../../types";
import { AGDialog } from "../AGDialog";
import TableSettingsCheckbox from "../TableSettingsCheckbox";

interface ITableFilterDialogProps {
	open: boolean;
	productType: EVENT_PRODUCT_TYPE_E;
	defaultValue?: number[];
	onClose: () => void;
	onChange: (deviceIds: number[]) => void;
}

const TableFilterDialog: FC<ITableFilterDialogProps> = (props) => {
	const { open, defaultValue, productType, onClose, onChange } = props;

	const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([]);

	// APIS
	// GET DEVICES LIST
	const { data: getDevicesListResponse } = useGetDevicesListQuery({ product_type: productType });

	const devicesList = useMemo(() => getDevicesListResponse ?? [], [getDevicesListResponse]);

	const selectAllState = useMemo<"none" | "all" | "partial">(() => {
		if (selectedDeviceIds.length === 0) return "none";
		if (selectedDeviceIds.length === devicesList.length) return "all";
		return "partial";
	}, [devicesList, selectedDeviceIds]);

	function handleChangeAllFilters(checked: boolean) {
		setSelectedDeviceIds((checked && devicesList.map((deviceItem) => deviceItem.id)) || []);
	}

	function handleChangeTableFilter(deviceId: number, checked: boolean) {
		setSelectedDeviceIds((currentList) => {
			const updatedList = [...currentList];

			if (!checked) return updatedList.filter((item) => item !== deviceId);
			if (!updatedList.includes(deviceId)) updatedList.push(deviceId);

			return updatedList;
		});
	}

	function handleApplyButtonClick() {
		if (selectedDeviceIds.length === devicesList.length) onChange([]);
		else onChange(selectedDeviceIds);
	}

	useEffect(() => {
		if (defaultValue) setSelectedDeviceIds(defaultValue);
		else setSelectedDeviceIds(devicesList.map((item) => item.id));
	}, [defaultValue, devicesList]);

	return (
		<AGDialog open={open} onClose={onClose} title="Table Filter" color="primary">
			<Typography marginBottom="1rem">Devices</Typography>

			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 6, md: 4 }}>
					<TableSettingsCheckbox
						label="Select All"
						checked={selectAllState === "all"}
						indeterminate={selectAllState === "partial"}
						onChange={(_event, checked) => handleChangeAllFilters(checked)}
					/>
				</Grid2>

				{devicesList.map((deviceItem) => (
					<Grid2 key={`table-filter-${deviceItem.id}`} size={{ xs: 6, md: 4 }}>
						<TableSettingsCheckbox
							label={deviceItem.device_name}
							checked={selectedDeviceIds.includes(deviceItem.id)}
							onChange={(_event, checked) => handleChangeTableFilter(deviceItem.id, checked)}
						/>
					</Grid2>
				))}

				<Grid2 size={12}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<LoadingButton disableElevation variant="contained" color="success" onClick={handleApplyButtonClick}>
							Apply
						</LoadingButton>
					</Box>
				</Grid2>
			</Grid2>
		</AGDialog>
	);
};

export default TableFilterDialog;
